import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/about';

export default function AboutPage() {
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formStatusMsg, setFormStatusMsg] = useState('');

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '关于我们' },
  ];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormStatusMsg('提交成功！感谢您的留言，我们会尽快回复。');
      setFormName('');
      setFormEmail('');
      setFormMessage('');
      return;
    }

    setFormStatus('loading');

    try {
      const body = new URLSearchParams();
      body.append('name', formName);
      body.append('email', formEmail);
      body.append('message', formMessage);

      const response = await fetch('https://readdy.ai/api/form/d9nfgsu8mbnljin59vrg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormStatusMsg('提交成功！感谢您的留言，我们会尽快回复。');
        setFormName('');
        setFormEmail('');
        setFormMessage('');
      } else {
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('success');
          setFormStatusMsg('提交成功！感谢您的留言。');
          setFormName('');
          setFormEmail('');
          setFormMessage('');
        } else {
          setFormStatus('error');
          setFormStatusMsg(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormStatusMsg('网络错误，请检查网络后重试。');
    }
  };

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '番茄视频',
    url: 'https://www.fanqie.video/',
    description: aboutInfo.intro,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: aboutInfo.contact.phone,
      email: aboutInfo.contact.email,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutInfo.contact.address,
    },
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="container-custom py-6">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <script type="application/ld+json">
          {JSON.stringify(orgLd)}
        </script>

        <section className="py-12">
          <div className="container-custom max-w-[900px]">
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-4">
              关于番茄视频
            </h1>
            <p className="text-lg text-foreground-600 mb-2 font-heading">
              {aboutInfo.tagline}
            </p>
            <p className="text-sm text-foreground-500 leading-relaxed mb-12 max-w-2xl">
              {aboutInfo.intro}
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-16">
              {aboutInfo.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-100 rounded-2xl p-6 text-center"
                >
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-1">{stat.value}</div>
                  <div className="text-xs text-foreground-500 whitespace-nowrap">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="mb-16">
              <h2 className="text-2xl font-bold font-heading text-foreground-900 mb-6">
                品牌使命
              </h2>
              <div className="bg-background-100 rounded-2xl p-8">
                <p className="text-base text-foreground-700 leading-relaxed mb-3">
                  <strong>使命：</strong>{aboutInfo.mission}
                </p>
                <p className="text-base text-foreground-700 leading-relaxed">
                  <strong>愿景：</strong>{aboutInfo.vision}
                </p>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-2xl font-bold font-heading text-foreground-900 mb-6">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-background-200"></div>
                <div className="flex flex-col gap-6">
                  {aboutInfo.milestones.map((m) => (
                    <div key={m.year} className="flex items-start gap-5 relative">
                      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full bg-primary-500 text-background-50 z-10">
                        <i className="ri-check-line text-sm"></i>
                      </div>
                      <div className="flex-1 pt-1.5">
                        <span className="text-sm font-bold text-primary-500 whitespace-nowrap">{m.year}</span>
                        <p className="text-sm text-foreground-600 mt-1 leading-relaxed">{m.event}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-16" id="testimonials">
              <h2 className="text-2xl font-bold font-heading text-foreground-900 mb-6">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {aboutInfo.testimonials.map((t) => (
                  <div key={t.name} className="bg-background-100 rounded-2xl p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-sm"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4 italic">
                      "{t.comment}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                        <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                      </div>
                      <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{t.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div id="contact">
              <h2 className="text-2xl font-bold font-heading text-foreground-900 mb-6">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="flex flex-col gap-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900 mb-0.5">邮箱</div>
                      <a href={`mailto:${aboutInfo.contact.email}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors">
                        {aboutInfo.contact.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900 mb-0.5">电话</div>
                      <a href={`tel:${aboutInfo.contact.phone}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors">
                        {aboutInfo.contact.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900 mb-0.5">地址</div>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900 mb-0.5">工作时间</div>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.workingHours}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-2xl p-6">
                  <h3 className="text-lg font-bold font-heading text-foreground-900 mb-4">发送留言</h3>
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="flex flex-col gap-4"
                  >
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={formEmail}
                        onChange={(e) => setFormEmail(e.target.value)}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={formMessage}
                        onChange={(e) => setFormMessage(e.target.value)}
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      />
                      <p className="text-xs text-foreground-400 mt-1 text-right">{formMessage.length}/500</p>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-xl text-sm whitespace-nowrap hover:bg-primary-600 transition-colors duration-200 cursor-pointer disabled:opacity-60"
                    >
                      {formStatus === 'loading' ? '提交中...' : '发送留言'}
                    </button>
                    {formStatus !== 'idle' && (
                      <p className={`text-sm ${formStatus === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                        {formStatusMsg}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}